import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/axios';
import { ReportCard } from '../components/ReportCard';
import styles from './ReportCardPrint.module.css';

interface ReportCardData {
  student: {
    id: number;
    name: string;
    student_id: string;
    form: number;
    school: string;
  };
  grades: Array<{
    subject: string;
    subject_code: string;
    marks: number;
    grade: string;
    position: number | null;
    remarks: string;
    teacher_remarks?: string;
    teacher_name?: string;
  }>;
  statistics: {
    total_marks: number;
    average_marks: number;
    position_in_class: number | null;
    total_students: number;
    subject_count: number;
    best_6_count?: number;
    overall_msce_grade: number | null;
    overall_remark: string | null;
  };
  settings: {
    term: number;
    year: number;
    term_start_date?: string;
    term_end_date?: string;
    next_term_opening?: string;
    date_of_release?: string;
    borders_fee?: string;
    day_scholar_fee?: string;
    principal_remarks?: string;
    grading_scale?: Array<{ position: number; range: string; remark?: string }>;
  };
  remarks?: {
    teacher: string;
    principal: string;
  };
}

/**
 * Stable print order: class position ascending, ungraded students last,
 * ties broken by student name so "Print All" always comes out in the same order.
 */
const sortReportCards = (cards: ReportCardData[]): ReportCardData[] =>
  [...cards].sort((a, b) => {
    const posA = a.statistics?.position_in_class ?? Number.MAX_SAFE_INTEGER;
    const posB = b.statistics?.position_in_class ?? Number.MAX_SAFE_INTEGER;
    if (posA !== posB) return posA - posB;
    return (a.student?.name || '').localeCompare(b.student?.name || '');
  });

export const ReportCardPrintPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reportCards, setReportCards] = useState<ReportCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Get parameters from URL
  const mode = searchParams.get('mode'); // 'single' or 'mass'
  const studentId = searchParams.get('studentId');
  const school = searchParams.get('school');
  const form = searchParams.get('form');
  const term = searchParams.get('term');
  const year = searchParams.get('year');

  useEffect(() => {
    loadReportCards();
  }, [mode, studentId, school, form, term, year]);

  const loadReportCards = async () => {
    try {
      setLoading(true);
      setError(null);

      if (mode === 'single' && studentId && term && year) {
        // Load single student report card
        const response = await api.get(`/api/report-cards/student/${studentId}`, {
          params: { school, term, year }
        });
        setReportCards([response.data]);
      } else if (mode === 'mass' && school && form && term && year) {
        // Load mass report cards
        const response = await api.get('/api/report-cards/mass', {
          params: { school, form, term, year }
        });
        setReportCards(sortReportCards(response.data.report_cards || []));
      } else {
        setError('Invalid parameters for report card generation');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load report cards');
      console.error('Error loading report cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading report cards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleBack} className={styles.backButton}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Action Bar (hidden during print) */}
      <div className={styles.actionBar}>
        <button onClick={handleBack} className={styles.backButton}>
          ← Back to Dashboard
        </button>
        <div className={styles.actionInfo}>
          <span className={styles.reportCount}>
            {reportCards.length} Report Card{reportCards.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button onClick={handlePrint} className={styles.printButton}>
          🖨️ Print All
        </button>
      </div>

      {/* Report Cards Container */}
      <div ref={printRef} className={styles.reportCardsContainer}>
        {reportCards.map((reportData, index) => (
          <ReportCard key={reportData.student?.id ?? index} data={reportData} />
        ))}
      </div>

      {reportCards.length === 0 && (
        <div className={styles.noData}>
          <p>No report cards available for the selected criteria.</p>
          <button onClick={handleBack} className={styles.backButton}>
            Back to Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

